// Versioned, compact URL state for the reproducibility tool. The dynamic multi-set state would need an unbounded
// number of fixed query params, so it is encoded into a single base64url `repro` param. Legacy two-set links
// (`pair`, `chipA`, `chipB`, `mode`) are still parsed for backwards compatibility.

import type { ReproducibilityAssayScope } from '@resistapp/common/api-types';

export interface ReproChipsState {
  mode: 'chips';
  chipIds: Array<number | null>;
}

export interface ReproSetsState {
  mode: 'sets';
  setLabels: string[];
  chipIds?: Array<number | null>;
  assayScope?: ReproducibilityAssayScope;
  groups: Array<Array<number | null>>;
}

export type ReproUrlState = ReproChipsState | ReproSetsState;

export const REPRO_STATE_PARAM = 'repro';
const VERSION_PREFIX = '1.';

interface CompactChips {
  m: 'c';
  c: Array<number | null>;
}

interface CompactSets {
  m: 's';
  l: string[];
  c?: Array<number | null>;
  a?: ReproducibilityAssayScope;
  g: Array<Array<number | null>>;
}

type Compact = CompactChips | CompactSets;

export function encodeReproState(state: ReproUrlState): string {
  const compact: Compact =
    state.mode === 'chips'
      ? { m: 'c', c: state.chipIds.map(nullableInt) }
      : {
          m: 's',
          l: state.setLabels,
          c: state.chipIds?.map(nullableInt),
          a: state.assayScope,
          g: state.groups.map(group => group.map(nullableInt)),
        };
  return `${VERSION_PREFIX}${base64UrlEncode(JSON.stringify(compact))}`;
}

export function decodeReproState(param: string | null): ReproUrlState | null {
  if (!param || !param.startsWith(VERSION_PREFIX)) {
    return null;
  }
  try {
    const json = base64UrlDecode(param.slice(VERSION_PREFIX.length));
    // The payload is untrusted user input, so keep the parsed shape loose and validate each branch at runtime.
    const compact = JSON.parse(json) as { m?: unknown; c?: unknown; l?: unknown; a?: unknown; g?: unknown };
    if (compact.m === 'c') {
      const chipIds = Array.isArray(compact.c) ? compact.c.map(nullableInt) : [];
      return chipIds.length ? { mode: 'chips', chipIds } : null;
    }
    if (compact.m === 's') {
      const setLabels = Array.isArray(compact.l) ? compact.l.map(label => String(label)) : [];
      const chipIds = Array.isArray(compact.c) ? compact.c.map(nullableInt) : undefined;
      const assayScope = compact.a === 'shared' || compact.a === 'all' ? compact.a : undefined;
      const groups = Array.isArray(compact.g)
        ? compact.g.map(group => (Array.isArray(group) ? group.map(nullableInt) : []))
        : [];
      return setLabels.length >= 2
        ? { mode: 'sets', setLabels, ...(chipIds ? { chipIds } : {}), ...(assayScope ? { assayScope } : {}), groups }
        : null;
    }
    return null;
  } catch {
    return null;
  }
}

// Legacy: ?mode=chips&chipA=3165&chipB=3034  or  ?pair=109108:111422&pair=...
export function parseLegacyReproParams(searchParams: URLSearchParams): ReproUrlState | null {
  const chipA = nullableInt(searchParams.get('chipA'));
  const chipB = nullableInt(searchParams.get('chipB'));
  const pairs = searchParams.getAll('pair');
  const modeParam = searchParams.get('mode');

  const parsedPairs = pairs
    .map(value => value.trim())
    .filter(value => value.length > 0)
    .map(value => {
      const [left, right] = value.split(':');
      return [nullableInt(left), nullableInt(right)] as Array<number | null>;
    });

  const preferChips = modeParam === 'chips' || ((chipA != null || chipB != null) && parsedPairs.length === 0);
  if (preferChips && (chipA != null || chipB != null)) {
    return { mode: 'chips', chipIds: [chipA, chipB] };
  }

  if (parsedPairs.length > 0) {
    return { mode: 'sets', setLabels: ['A', 'B'], groups: parsedPairs };
  }

  return null;
}

function nullableInt(value: unknown): number | null {
  if (value == null) return null;
  if (typeof value === 'number') {
    return Number.isSafeInteger(value) ? value : null;
  }
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (trimmed === '' || !/^\d+$/.test(trimmed)) return null;
  const parsed = Number(trimmed);
  return Number.isSafeInteger(parsed) ? parsed : null;
}

function base64UrlEncode(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let binary = '';
  bytes.forEach(byte => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlDecode(encoded: string): string {
  const padded = encoded.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, char => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}
